// src/features/aspects/logic.ts
// Framework-agnostic business logic for Aspects tab.
// Zero React imports — pure TypeScript functions.

import type { Aspect } from '@core/types/task/Aspect';
import type { AspectCardData } from './types';

const DEFAULT_ASPECT_ICON = 'M4 6h16M4 12h16M4 18h16';

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

export function toAspectCardData(aspect: Aspect): AspectCardData {
  const typeName = extractTypeName(aspect);
  return {
    id: aspect.entity?.id || '',
    typeId: aspect.entity?.type_id || '',
    urn: aspect.urn || '',
    typeName,
    title: aspect.title || 'Untitled',
    subtitle: aspect.subtitle || '',
    icon: aspect.icon || DEFAULT_ASPECT_ICON,
    color: aspect.color || 'text-muted-foreground',
    status: aspect.status || '',
    statusVariant: getAspectStatusVariant(aspect.status),
    updatedAt: aspect.updated_at ? String(aspect.updated_at) : '',
    previewFields: {},
  };
}

function extractTypeName(aspect: Aspect): string {
  // Type encoded in entity.type_name: "task_aspect" → "task"
  const typeName = aspect.entity?.type_name || '';
  return typeName.replace(/_aspect$/, '') || 'unknown';
}

function getAspectStatusVariant(status: string): string {
  if (!status) return 'secondary';
  const lower = status.toLowerCase();
  if (lower.includes('active') || lower.includes('open') || lower.includes('in_progress')) return 'primary';
  if (lower.includes('done') || lower.includes('completed')) return 'success';
  if (lower.includes('blocked') || lower.includes('cancelled')) return 'error';
  if (lower.includes('pending') || lower.includes('draft')) return 'secondary';
  return 'default';
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export function sortAspects(aspects: Aspect[]): Aspect[] {
  return [...aspects].sort((a, b) => {
    const aTime = a.updated_at ? new Date(String(a.updated_at)).getTime() : 0;
    const bTime = b.updated_at ? new Date(String(b.updated_at)).getTime() : 0;
    return bTime - aTime;
  });
}

// ---------------------------------------------------------------------------
// Type display helpers
// ---------------------------------------------------------------------------

export function getAspectStatusConfig(
  _typeName: string,
  status: string,
): { variant: string; labelKey: string } {
  // Generic mapping — profile services can override via manifest
  const variant = getAspectStatusVariant(status);
  return { variant, labelKey: `rail.aspects.status.${status.toLowerCase()}` };
}
